import React, { useState, useMemo, useCallback } from "react";
import { usePostHogTracking } from "../hooks/usePostHogTracking";
import { timeAgo } from "../utils/timeUtils";

const TAG_REGEX = /\{\{(species|family|product|category|brand):([^}]+)\}\}/g;

function parseDigestText(text, dataType, onTagClick) {
  if (!text) return null;
  const parts = [];
  let lastIndex = 0;
  let match;
  const regex = new RegExp(TAG_REGEX.source, "g");

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const [, tagType, tagContent] = match;
    const separatorIndex = tagContent.lastIndexOf("|");
    if (separatorIndex <= 0 || separatorIndex === tagContent.length - 1) {
      parts.push(match[0]);
      lastIndex = regex.lastIndex;
      continue;
    }
    const value = tagContent.slice(0, separatorIndex);
    const displayText = tagContent.slice(separatorIndex + 1);
    const key = `${tagType}-${value}-${match.index}`;
    parts.push(
      <button
        key={key}
        className="bg-transparent border-none text-blue-600 font-medium cursor-pointer p-0 underline decoration-blue-300 underline-offset-2 hover:text-blue-800 hover:decoration-blue-600 transition-colors"
        style={{ fontSize: "inherit" }}
        onClick={() => onTagClick(tagType, value, displayText)}
      >
        {displayText}
      </button>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

export default function PriceDigest({ dataType, digestData, onFilterApply }) {
  const [activeTab, setActiveTab] = useState("daily");
  const [collapsed, setCollapsed] = useState(false);
  const { trackEvent } = usePostHogTracking();

  const handleTagClick = useCallback((tagType, value, displayText) => {
    trackEvent("price_digest_link_click", {
      tag_type: tagType,
      value: value,
      display_text: displayText,
      digest_type: activeTab,
      data_type: dataType,
    });
    if (onFilterApply) {
      onFilterApply(tagType, value, displayText);
    }
  }, [activeTab, dataType, onFilterApply, trackEvent]);

  const dailyContent = useMemo(
    () => parseDigestText(digestData?.daily, dataType, handleTagClick),
    [digestData?.daily, dataType, handleTagClick]
  );
  const weeklyContent = useMemo(
    () => parseDigestText(digestData?.weekly, dataType, handleTagClick),
    [digestData?.weekly, dataType, handleTagClick]
  );

  if (!digestData || (!digestData.daily && !digestData.weekly)) {
    return null;
  }

  const activeContent = activeTab === "daily" ? dailyContent : weeklyContent;
  const hasDaily = !!digestData.daily;
  const hasWeekly = !!digestData.weekly;
  const activeTimestamp = activeTab === "daily"
    ? digestData.daily_generated_at
    : digestData.weekly_generated_at;
  const timeAgoLabel = timeAgo(activeTimestamp);

  if (!activeContent) {
    if (activeTab === "daily" && hasWeekly) {
      setActiveTab("weekly");
    } else if (activeTab === "weekly" && hasDaily) {
      setActiveTab("daily");
    } else {
      return null;
    }
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 to-green-50 border border-blue-200 rounded-xl mb-4 overflow-hidden">
      <div
        className="flex justify-between items-center px-3 py-2 cursor-pointer select-none"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">🐠</span>
          <h3 className="text-sm font-semibold text-blue-800 m-0">
            {dataType === "products" ? "Shelf Watch" : "Catch of the Day"}
          </h3>
          <button className="bg-transparent border-none text-gray-500 text-xs cursor-pointer p-0 leading-none">
            {collapsed ? "▼" : "▲"}
          </button>
        </div>
        {!collapsed && hasDaily && hasWeekly && (
          <div
            className="flex gap-1 bg-blue-100 rounded-md p-0.5"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={`px-3 py-1 border-none rounded text-xs font-medium cursor-pointer transition-all ${
                activeTab === "daily"
                  ? "bg-white text-blue-800 shadow-sm"
                  : "bg-transparent text-blue-500"
              }`}
              onClick={() => {
                setActiveTab("daily");
                trackEvent("price_digest_tab_switch", { tab: "daily", data_type: dataType });
              }}
            >
              Today
            </button>
            <button
              className={`px-3 py-1 border-none rounded text-xs font-medium cursor-pointer transition-all ${
                activeTab === "weekly"
                  ? "bg-white text-blue-800 shadow-sm"
                  : "bg-transparent text-blue-500"
              }`}
              onClick={() => {
                setActiveTab("weekly");
                trackEvent("price_digest_tab_switch", { tab: "weekly", data_type: dataType });
              }}
            >
              This Week
            </button>
          </div>
        )}
      </div>
      {!collapsed && (
        <div className="px-3 pb-2">
          <p className="text-sm leading-relaxed text-gray-700 m-0">{activeContent}</p>
          {timeAgoLabel && (
            <p className="text-xs text-gray-400 mt-2 mb-0 text-right">
              Updated {timeAgoLabel}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
